export const PGS_LANGUAGE_ID = "pgs"

const KEYWORDS = [
  "CREATE", "NODE", "EDGE", "GRAPH", "TYPE",
  "STRICT", "LOOSE", "ABSTRACT", "IMPORTS",
  "OPEN", "OPTIONAL"
]

export const monarch = {
  defaultToken: "",
  ignoreCase: true,
  brackets: [
    { open: "(", close: ")", token: "delimiter.parenthesis" },
    { open: "[", close: "]", token: "delimiter.square" },
    { open: "{", close: "}", token: "delimiter.curly" }
  ],
  keywords: KEYWORDS.map(k => k.toLowerCase()),
  tokenizer: {
    root: [
      [/[[\](){}]/, "@brackets"],
      [/\s+/, "white"],
      [/[,:;>-]/, "delimiter"],
      [/\b(create|node|edge|graph|type|strict|loose|abstract|imports|open|optional)\b/, "keyword"],
      [/[A-Za-z0-9_-]+/, "identifier"],
    ]
  }
}

const hasBalanced = (text, open, close) => {
  let c = 0
  for (const ch of text) {
    if (ch === open) c++
    else if (ch === close) c--
    if (c < 0) return false
  }
  return c === 0
}

export function validatePGS(text) {
  const markers = []
  const lines = text.split("\n")

  const addMarkerAtIndex = (index, message, severity = 4) => {
    let acc = 0
    let line = 1
    let col = 1
    for (let i = 0; i < lines.length; i++) {
      const len = lines[i].length + 1 // +1 for '\n'
      if (acc + len > index) {
        line = i + 1
        col = index - acc + 1
        break
      }
      acc += len
    }

    markers.push({
      severity,
      message,
      startLineNumber: line,
      startColumn: col,
      endLineNumber: line,
      endColumn: col + 1,
    })
  }

  const balances = [
    { open: "(", close: ")", msg: "Unbalanced parentheses" },
    { open: "[", close: "]", msg: "Unbalanced brackets" },
    { open: "{", close: "}", msg: "Unbalanced braces" },
  ]
  balances.forEach(({ open, close, msg }) => {
    if (!hasBalanced(text, open, close)) {
      markers.push({
        severity: 8,
        message: msg,
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: Math.max(1, lines.length),
        endColumn: (lines[lines.length - 1] || "").length + 1,
      })
    }
  })

  const reAfterCreate = /\bCREATE\b\s+([A-Za-z0-9_-]+)/gi
  let mc
  while ((mc = reAfterCreate.exec(text)) !== null) {
    const word = mc[1]
    if (!/^(NODE|EDGE|GRAPH)$/i.test(word)) {
      const wordStart = mc.index + mc[0].length - word.length
      addMarkerAtIndex(
        wordStart,
        "Expected 'NODE', 'EDGE', or 'GRAPH' after 'CREATE'.",
        4
      )
    }
  }

  const reCreateHead =
    /\bCREATE\b\s+(NODE|EDGE|GRAPH)\b\s+([A-Za-z0-9_-]+)/gi
  let mh
  while ((mh = reCreateHead.exec(text)) !== null) {
    const kw = mh[1]      // NODE/EDGE/GRAPH
    const word = mh[2]    // should be TYPE
    if (!/^TYPE$/i.test(word)) {
      const wordStart = mh.index + mh[0].length - word.length
      addMarkerAtIndex(
        wordStart,
        `Expected 'TYPE' after '${kw}'.`,
        4
      )
    }
  }

  lines.forEach((ln, i) => {
    if (/\)\s*-\s*\[/.test(ln) && !/->/.test(ln)) {
      markers.push({
        severity: 4,
        message: "Edge is missing '>' in '->'",
        startLineNumber: i + 1,
        startColumn: 1,
        endLineNumber: i + 1,
        endColumn: ln.length + 1,
      })
    }
  })

  const reBadOptional = /\bOPTION(?!AL\b)[A-Za-z0-9_-]*/g
  let mo
  while ((mo = reBadOptional.exec(text)) !== null) {
    addMarkerAtIndex(
      mo.index,
      "Unknown keyword. Did you mean 'OPTIONAL'?",
      4
    )
  }

  const trimmed = text.trim()
  if (trimmed.length > 0) {
    const lastSemi = text.lastIndexOf(";")
    const lastBrace = text.lastIndexOf("}")
    const lastEnd = Math.max(lastSemi, lastBrace)

    if (lastEnd !== -1 && lastEnd < text.length - 1) {
      let i = lastEnd + 1
      while (i < text.length && /\s/.test(text[i])) i++
      if (i < text.length) {
        addMarkerAtIndex(
          i,
          "Unexpected text after end of statement. PG-Schema must end at EOF.",
          4
        )
      }
    }
  }

  return markers
}



const TYPE_KEYWORDS = [
  "STRING",
  "INT",
  "BIGINT",
  "FLOAT",
  "DOUBLE",
  "DECIMAL",
  "BOOLEAN",
  "DATE",
  "TIME",
  "DATETIME",
  "TIMESTAMP",
  "UUID",
  "JSON",
]

const SNIPPET_START = String.fromCharCode(36, 123)

export const completionItems = [
  {
    label: "CREATE GRAPH TYPE",
    kind: 14,
    insertText:
`CREATE GRAPH TYPE ${"${"}1:GraphName} ${"${"}2|STRICT,LOOSE|}
{
  ${"${"}0}
};`,
    insertTextRules: 4, 
    documentation: "Create a graph type (STRICT or LOOSE).",
  },
  {
    label: "CREATE NODE TYPE",
    kind: 14,
    insertText:
`CREATE NODE TYPE (${"${"}1:NodeType} : ${"${"}2:Label} {
  ${"${"}3:prop} ${"${"}4:Type}, OPTIONAL ${"${"}5:optProp} ${"${"}6:Type}
});`,
    insertTextRules: 4,
    documentation: "Create a node type with label(s) and properties.",
  },
  {
    label: "CREATE EDGE TYPE",
    kind: 14,
    insertText:
`CREATE EDGE TYPE (${"${"}1:Src}) - [ ${"${"}2:EdgeType} : ${"${"}3:Label} { ${"${"}4:prop} ${"${"}5:Type}, OPTIONAL ${"${"}6:optProp} ${"${"}7:Type} } ] -> (${"${"}8:Dst});`,
    insertTextRules: 4,
    documentation: "Create an edge type.",
  },
  {
    label: "property",
    kind: 14,
    insertText: `${SNIPPET_START}1:name} ${SNIPPET_START}2:STRING}`,
    insertTextRules: 4,
    documentation: "Property definition: name TYPE",
  },

  { label: "STRICT",   kind: 14, insertText: "STRICT" },
  { label: "LOOSE",    kind: 14, insertText: "LOOSE" },
  { label: "ABSTRACT", kind: 14, insertText: "ABSTRACT" },
  { label: "IMPORTS",  kind: 14, insertText: "IMPORTS " },
  { label: "OPEN",     kind: 14, insertText: "OPEN" },
  { label: "OPTIONAL", kind: 14, insertText: "OPTIONAL " },

  ...TYPE_KEYWORDS.map((t) => ({
    label: t,
    kind: 14,
    insertText: t,
    documentation: `Type: ${t}`,
  })),
]

export function provideCodeActions(model, range) {
  const text = model.getValueInRange(range)
  const actions = []

  if (/\bCREATE\s+(NODE|EDGE|GRAPH)\b(?![^]*?\bTYPE\b)/i.test(text)) {
    actions.push({
      title: "Insert 'TYPE'",
      diagnostics: [],
      kind: "quickfix.insertType",
      edit: {
        edits: [
          {
            resource: model.uri,
            edit: {
              range,
              text: text.replace(/\bCREATE\s+(NODE|EDGE|GRAPH)\b/i, (m) => `${m} TYPE`)
            }
          }
        ]
      },
      isPreferred: true
    })
  }

  if (/-\s*\[/.test(text) && !/->/.test(text)) {
    actions.push({
      title: "Insert '->'",
      diagnostics: [],
      kind: "quickfix.insertArrow",
      edit: {
        edits: [
          {
            resource: model.uri,
            edit: {
              range,
              text: text.replace(/-\s*\[/, "- [").replace(/\]\s*\)/, "] -> ()")
            }
          }
        ]
      }
    })
  }

  return { actions, dispose: () => {} }
}
